import {
  selectProduct,
  injectCustomerProductDetails,
  loadProductsRegistry,
  loadProducts,
  getSelectedProductRegistry,
  CARD_PRODUCT,
} from '@americanexpress/product-ducks';
import { Map as iMap } from 'immutable';
import { composeModules } from 'holocron';
import type { Store } from 'redux';
import { queryProcedureResult } from 'iguazu-rpc';
import {
  queryCredoPreferences,
  queryCredoRampup,
  CredoEligibilityResponse,
} from '@americanexpress/customer-dashboard-utils';

import { getUserRoles } from '../selectors';
import { NAV_TOOLTIP_DISMISSED } from '../constants';

const injectExternalProductData = (
  { dispatch, getState }: Store,
  { productsLocation = 'products' }: ConsumerNavigationPropsFull
): void => {
  const state = getState();
  const rootModuleName = state.getIn(['config', 'rootModuleName']);
  const productState = state.getIn(['modules', rootModuleName, productsLocation], iMap()).toJS();
  if (productState.selectedProduct) {
    dispatch(selectProduct(productState.selectedProduct));
  }
  if (productState.details && productState.details.types) {
    const types = Object.entries<ProductDetailsEntry>(productState.details.types)
      .filter(([, { productsOrder }]) => productsOrder.length > 0);
    const populatedTypes = Object.fromEntries(types);
    if (Object.keys(populatedTypes).length > 0) {
      dispatch(injectCustomerProductDetails(populatedTypes));
    }
  }
};

const loadProductData = async ({ dispatch, getState }: Store): Promise<void> => {
  await dispatch(loadProductsRegistry());
  const { type: selectedProductType } = getSelectedProductRegistry(getState()) || {};
  if (selectedProductType === CARD_PRODUCT) {
    await dispatch(loadProducts({ types: [CARD_PRODUCT] }));
  }
};

const loadProductNavigationLinks = (
  { dispatch, getState }: Store,
  { view, locale }: ConsumerNavigationPropsFull
): Promise<void> | Promise<unknown> => {
  let productNavigationLinksPromise;
  const state = getState();
  const { opaqueAccountId, type: productType } = getSelectedProductRegistry(state) || {};
  const roles = getUserRoles(state);

  if (view === 'PRODUCT' && opaqueAccountId) {
    ({ promise: productNavigationLinksPromise } = dispatch(queryProcedureResult({
      procedureName: 'readProductNavigationLinksV1',
      args: {
        body: {
          opaqueAccountId,
          productType,
          locale,
          roles,
        },
      },
    })));
  } else {
    productNavigationLinksPromise = Promise.resolve();
  }
  return productNavigationLinksPromise;
};

const loadSubModules = (
  { dispatch }: Store,
  { view }: ConsumerNavigationPropsFull
): Promise<(void | Error)[]> => dispatch(composeModules([
  { name: 'axp-consumer-context-switcher', props: { view } },
  { name: 'axp-site-area-nav-container' },
]));

const loadTooltipPreference = (
  store: Store,
  { view }: ConsumerNavigationPropsFull
): Promise<void> | Promise<unknown> => {
  let tooltipPreferencesPromise;
  if (view === 'ACCOUNT') {
    ({ promise: tooltipPreferencesPromise } = queryCredoPreferences({
      store,
      types: [NAV_TOOLTIP_DISMISSED],
      timeout: 3000,
    }));
  } else {
    tooltipPreferencesPromise = Promise.resolve();
  }

  return tooltipPreferencesPromise;
};

const loadCredoEligibility = (
  store: Store
): Promise<CredoEligibilityResponse> => {
  const { promise: eligibilityPromise } = queryCredoRampup({
    store,
  });

  return eligibilityPromise;
};

export {
  injectExternalProductData,
  loadProductData,
  loadProductNavigationLinks,
  loadSubModules,
  loadTooltipPreference,
  loadCredoEligibility,
};
